import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Panel, ListGroup, ListGroupItem, Button } from 'react-bootstrap';

import { inject, bind } from '../../utils/decorators';
import { ApiStore } from '../../stores/ApiStore';
import { UiStore } from '../../stores/UiStore';
import { IArbeitsmittel, IVorgangArbeitsmittelResponse } from '../../models/ApiTypes';
import FaIcon from '../site/FaIcon';
import Callout from '../site/Callout';

interface IArbeitsmittelListeProps {
  vorgangId: string;
  editable?: boolean;
}

@observer
export default class ArbeitsmittelListe extends React.Component<IArbeitsmittelListeProps, {}> {

  static defaultProps: Partial<IArbeitsmittelListeProps> = {
    editable: true
  };

  @inject(ApiStore) apiStore: ApiStore;
  @inject(UiStore) uiStore: UiStore;
  readonly arbeitsmittel = observable<IArbeitsmittel>([]);

  async componentDidMount() {
    const { vorgangId } = this.props;
    let arbeitsmittel = await this.apiStore.vorgangArbeitsmittel(vorgangId);
    if (arbeitsmittel.ok) {
      this.setArbeitsmittel(arbeitsmittel.json);
    }
  }

  @action setArbeitsmittel(arbeitsmittel: IVorgangArbeitsmittelResponse) {
    this.arbeitsmittel.clear();
    arbeitsmittel.forEach(item => {
      this.arbeitsmittel.push(item);
    });
  }

  @bind async handleArbeitsmittelEntfernen(arbeitsmittel: IArbeitsmittel) {
    const { vorgangId } = this.props;
    const response = await this.apiStore.vorgangArbeitsmittelEntfernen(vorgangId, arbeitsmittel.arbeitsmittelguid);
    if (response.ok && response.json.success) {
      this.removeItem(arbeitsmittel);
    }
  }

  @action removeItem(arbeitsmittel: IArbeitsmittel) {
    this.arbeitsmittel.remove(arbeitsmittel);
  }

  render() {
    const { isLoading } = this.apiStore;
    const { editable } = this.props;
    return (
      <Panel
        header={this.arbeitsmittel.length > 0 ? `Arbeitsmittel (${this.arbeitsmittel.length} gewählt)` : 'Arbeitsmittel'}
        bsStyle={this.arbeitsmittel.length > 0 ? 'success' : 'danger'}
        collapsible={true}
        defaultExpanded={true}
        footer={editable && (
          <div className="text-right">
            <Button onClick={() => this.uiStore.navigateTo('arbeitsmittel')}>Auswählen</Button>
          </div>
        )}
      >
        <ListGroup fill={true}>
          {(this.arbeitsmittel.length === 0) ? (
            <ListGroupItem>{isLoading ? 'lade Arbeitsmittel...' : 'Keine Arbeitsmittel ausgewählt'}</ListGroupItem>
          ) : this.arbeitsmittel.map((arbeitsmittel, index) => (
            <ArbeitsmittelItem
              key={index}
              editable={editable}
              arbeitsmittel={arbeitsmittel}
              onArbeitsmittelEntfernen={this.handleArbeitsmittelEntfernen}
            />
          ))}
        </ListGroup>
      </Panel>
    );
  }
}

interface IArbeitsmittelItemProps {
  arbeitsmittel: IArbeitsmittel;
  editable?: boolean;
  detailansicht?: boolean;
  onArbeitsmittelEntfernen?: (arbeitsmittel: IArbeitsmittel) => void;
}

export class ArbeitsmittelItem extends React.Component<IArbeitsmittelItemProps, {}> {

  static defaultProps: Partial<IArbeitsmittelItemProps> = {
    editable: true
  };

  @inject(UiStore) uiStore: UiStore;

  render() {
    const { arbeitsmittel, onArbeitsmittelEntfernen, editable, detailansicht } = this.props;

    let statusText = arbeitsmittel.amstatus;

    let className;
    switch (arbeitsmittel.amstatus.toLowerCase()) {
      case 'aktiv':
        className = 'text-success';
        break;
      case 'qualifi':
        className = 'text-warning';
        statusText = 'In Qualifizierung';
        break;
      case 'gesperrt':
        className = 'text-danger';
        break;
      default:
        className = 'text-muted';
        break;
    }

    if (!detailansicht) {
      if (arbeitsmittel.gueltigtage && arbeitsmittel.gueltigtage < 0) {
        className = 'text-danger';
        statusText = 'Abgelaufen';
      }
    }

    let hinweis = <div/>;
    if (arbeitsmittel.gueltigtage) {
      if (arbeitsmittel.gueltigtage <= 0) {
          hinweis = <Callout bsStyle="danger">Seit {Math.abs(arbeitsmittel.gueltigtage)} Tagen abgelaufen, bitte wenden Sie sich an die DIQ Zert GmbH.</Callout>;
        } else {
        if (arbeitsmittel.gueltigtage < 90) {
          hinweis = <Callout bsStyle="warning">Läuft in {Math.abs(arbeitsmittel.gueltigtage)} Tagen ab, bitte wenden Sie sich an die DIQ Zert GmbH.</Callout>;
        }
      }
    }

    return (
      <ListGroupItem>
        {!detailansicht && (
          <span>{arbeitsmittel.arbeitsmittelnr} - {arbeitsmittel.name} (<span className={className}>{statusText}</span>)</span>
        )}
        {detailansicht && (
          <span>
            <b>{arbeitsmittel.arbeitsmittelnr} - {arbeitsmittel.name}</b>
            {/* Button für Rückmeldung nur in Arbeitsmittelübersicht einblenden */}
            <span className="pull-right">
              <Button
                className="btn-flat"
                title="Rückmeldung zu diesem Arbeitsmittel verfassen"
                onClick={() => this.uiStore.navigateTo(`/arbeitsmittel/${arbeitsmittel.arbeitsmittelguid}/feedback`)}
              >
                <FaIcon icon="envelope-o" />
              </Button>
            </span>
            <div className="text-muted">
              <span className={className}>{arbeitsmittel.amstatus}</span>, {arbeitsmittel.giltbis ? 'gültig bis: ' + arbeitsmittel.giltbis : 'unbefristet'}
              <br/>
              {hinweis}
            </div>
          </span>
        )}
        {onArbeitsmittelEntfernen && editable && (
          <Button
            className="btn-flat pull-right"
            onClick={() => onArbeitsmittelEntfernen(arbeitsmittel)}
            title="Arbeitsmittel entfernen"
          >
            <FaIcon icon="trash-o" />
          </Button>
        )}
      </ListGroupItem>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/arbeitsmittel/ArbeitsmittelListe.tsx